import { useCallback } from 'react';

import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type UsePublicShowcaseNavBoardNavigationParams = {
  boardId: string;
  /** The page this board is listed under — `null` for Home/the root page. */
  pageId: string | null;
  /** The listing page's canonical slug, or `null` for Home — see `PublicShowcaseNavEntry.slug`. */
  slug: string | null;
};

export type PublicShowcaseNavBoardNavigation = {
  /**
   * The public board route this board opens — carries `?page={slug}` when opened from a sub-page.
   * `undefined` when the mounting surface's navigation has no URLs (the collection link editor's
   * Edit tab): rows then render buttons that call `handleSelect` instead of real links.
   */
  href: string | undefined;
  /** Navigate the viewer into the board, however the mounting surface navigates. */
  handleSelect: () => void;
};

/**
 * The navigation decision for one board row in a page's nav dropdown, shared by the desktop bar and
 * the mobile menu so the two can never disagree.
 *
 * The route is built from the HOVERED entry's own slug — not the viewer's current page — so opening
 * a board from the "Case Studies" dropdown always carries `?page=case-studies`, whatever page the
 * viewer is on — the ONE drill that passes the contract's explicit `page` override instead of
 * letting the implementation resolve the current page. A Home board opens the flat
 * `/a/{shortId}/b/{boardId}` with no page trail.
 */
export const usePublicShowcaseNavBoardNavigation = ({
  boardId,
  pageId,
  slug,
}: UsePublicShowcaseNavBoardNavigationParams): PublicShowcaseNavBoardNavigation => {
  const { getBoardNavigationHref, onNavigateToBoard } = usePublicShowcaseContext();

  const handleSelect = useCallback(() => {
    void onNavigateToBoard({ boardId, page: { pageId, pageSlug: slug } });
  }, [boardId, onNavigateToBoard, pageId, slug]);

  return {
    href: getBoardNavigationHref?.({ boardId, pageSlug: slug }),
    handleSelect,
  };
};
